/** «Куда планируем» вкладки «Подбор поставки»: какие регионы WB — цели поставки. Выключенный
 *  регион даёт 0: не входит в суммы товара, в «Заполнить до N дней» и в выгрузку; в таблице скрыт,
 *  пока не включён «показывать выключенные» (тогда — серым).
 *
 *  Умолчание (владелец, 19.10.2026): регионы РФ (`region_kind = macro`) включены, страны и «не
 *  определён» — выключены. Храним только ОТКЛОНЕНИЯ от умолчания — `off` (выключенные регионы РФ) и
 *  `on` (включённые страны / «не определён»): регион, впервые появившийся в витрине, получает
 *  умолчание своего вида, а не «выключен, потому что его не было в списке». Настройка личная — в
 *  localStorage браузера (`{v: 1, off, on}`); не прочиталась — умолчание и текст на экране. */
import type { WbOffer } from "@/api/wbRegions";
import { regionLabel } from "./matrix";
import { browserStorage, regionId, type StorageLike } from "./supplyDraft";

export const TARGETS_STORAGE_KEY = "seller-helper:wb-regions:supply-targets:v1";
const TARGETS_VERSION = 1;

export interface Targets {
  /** выключенные регионы РФ (id региона) */
  off: ReadonlySet<string>;
  /** включённые страны и «не определён» (id региона) */
  on: ReadonlySet<string>;
}

export const DEFAULT_TARGETS: Targets = { off: new Set(), on: new Set() };

/** Регион в списке целей: id (supplyDraft.regionId), подпись, регион РФ или нет. */
export interface RegionOpt {
  id: string;
  label: string;
  macro: boolean;
}

export function isOn(t: Targets, r: { id: string; macro: boolean }): boolean {
  return r.macro ? !t.off.has(r.id) : t.on.has(r.id);
}

/** Регионы из данных вкладки: сначала РФ, потом страны и «не определён»; внутри — по имени. */
export function regionOptions(offers: WbOffer[]): RegionOpt[] {
  const seen = new Map<string, RegionOpt>();
  for (const o of offers) {
    for (const c of o.regions) {
      const id = regionId(c);
      if (!seen.has(id)) seen.set(id, { id, label: regionLabel(c), macro: c.region_kind === "macro" });
    }
  }
  return [...seen.values()].sort((a, b) => Number(b.macro) - Number(a.macro) || a.label.localeCompare(b.label, "ru"));
}

/** Включить / выключить набор регионов. */
export function setTargets(t: Targets, regions: RegionOpt[], enabled: boolean): Targets {
  const off = new Set(t.off);
  const on = new Set(t.on);
  for (const r of regions) {
    if (r.macro) {
      if (enabled) off.delete(r.id);
      else off.add(r.id);
    } else if (enabled) on.add(r.id);
    else on.delete(r.id);
  }
  return { off, on };
}

/** Состояние групповой галочки: все включены / ни одного / часть (indeterminate). */
export type GroupState = "all" | "none" | "some";

export function groupState(t: Targets, regions: RegionOpt[]): GroupState {
  const n = regions.filter((r) => isOn(t, r)).length;
  if (n === 0) return "none";
  return n === regions.length ? "all" : "some";
}

// ─────────────── localStorage ───────────────

export interface TargetsLoad {
  targets: Targets;
  /** не прочитано — текст для экрана (тогда умолчание) */
  error: string | null;
}

const strings = (x: unknown): x is string[] => Array.isArray(x) && x.every((v) => typeof v === "string");

export function loadTargets(getStorage: () => StorageLike = browserStorage): TargetsLoad {
  let raw: string | null;
  try {
    raw = getStorage().getItem(TARGETS_STORAGE_KEY);
  } catch (e) {
    return { targets: DEFAULT_TARGETS, error: `«Куда планируем» не прочитано из браузера (${String(e)}) — по умолчанию` };
  }
  if (raw === null) return { targets: DEFAULT_TARGETS, error: null };
  let p: unknown;
  try {
    p = JSON.parse(raw);
  } catch {
    return { targets: DEFAULT_TARGETS, error: "«Куда планируем» в браузере повреждено (не JSON) — по умолчанию" };
  }
  const x = p as { v?: unknown; off?: unknown; on?: unknown } | null;
  if (typeof x !== "object" || x === null || x.v !== TARGETS_VERSION || !strings(x.off) || !strings(x.on)) {
    return { targets: DEFAULT_TARGETS, error: "«Куда планируем» в браузере в неизвестном формате — по умолчанию" };
  }
  return { targets: { off: new Set(x.off), on: new Set(x.on) }, error: null };
}

/** Сохранить; ошибка — текст для экрана (настройка живёт, пока открыта вкладка «Подбор поставки»). */
export function saveTargets(t: Targets, getStorage: () => StorageLike = browserStorage): string | null {
  try {
    const s = getStorage();
    if (t.off.size === 0 && t.on.size === 0) s.removeItem(TARGETS_STORAGE_KEY);
    else s.setItem(TARGETS_STORAGE_KEY, JSON.stringify({ v: TARGETS_VERSION, off: [...t.off], on: [...t.on] }));
    return null;
  } catch (e) {
    return `«Куда планируем» не сохраняется в браузере (${String(e)}) — сбросится при закрытии или переключении вкладки/кабинета`;
  }
}
